import React, { useEffect, useRef, useState } from 'react';

import CliveMarkdownDisplay from '~/components/Clive/CliveMarkdownDisplay/CliveMarkdownDisplay';
import { Accordion, AccordionDetails, AccordionSummary } from '~/components/core/Accordion';
import Text from '~/components/core/TextComponents/Text';
import CheckIcon from '~/components/icons/CheckIcon';
import CloseIcon from '~/components/icons/CloseIcon';
import colors from '~/theme/tailwind/colors';
import cn from '~/Utils/cn';

import type { AgentTimelineEntry, AgentTimelineEntryProps } from './types';

// Sized to sit inside the 12px dot.
const MARKER_ICON_SIZE = 8;

interface EntryStyle {
  dot: string;
  title: string;
  titleWeight: string;
  icon?: React.ReactNode;
}

// `default` is the status-less entry AI chat renders, and must keep looking exactly as it did.
const STYLE_BY_STATUS: Record<NonNullable<AgentTimelineEntry['status']> | 'default', EntryStyle> = {
  default: { dot: 'border-blue-600 bg-blue-300', title: 'text-grey-600', titleWeight: Text.WEIGHTS.SEMI_BOLD },
  pending: { dot: 'border-grey-200 bg-slate-500', title: 'text-grey-300', titleWeight: Text.WEIGHTS.REGULAR },
  in_progress: {
    dot: 'animate-pulse border-blue-600 bg-blue-300',
    title: 'text-grey-600',
    titleWeight: Text.WEIGHTS.REGULAR,
  },
  completed: {
    dot: 'border-blue-600 bg-blue-300',
    title: 'text-grey-600',
    titleWeight: Text.WEIGHTS.REGULAR,
    icon: <CheckIcon size={MARKER_ICON_SIZE} iconColor={colors.blue['800']} />,
  },
  // Only the marker carries the error colour; the label stays default so a failed row reads as part
  // of the same list rather than as a block of red text.
  error: {
    dot: 'border-status-failed bg-status-failed',
    title: 'text-grey-600',
    titleWeight: Text.WEIGHTS.REGULAR,
    icon: <CloseIcon size={MARKER_ICON_SIZE} iconColor={colors.white} />,
  },
};

const AgentTimelineEntryDisplay: React.FC<AgentTimelineEntryProps> = ({ entry, isLast, defaultExpanded = true }) => {
  const hasContent = !!entry.content;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const wasExpandedRef = useRef(defaultExpanded);

  useEffect(() => {
    if (wasExpandedRef.current && !defaultExpanded) {
      setExpanded(false);
    } else if (!wasExpandedRef.current && defaultExpanded) {
      setExpanded(true);
    }
    wasExpandedRef.current = defaultExpanded;
  }, [defaultExpanded]);

  const handleToggle = () => {
    if (hasContent) {
      setExpanded(!expanded);
    }
  };

  const style = STYLE_BY_STATUS[entry.status ?? 'default'];
  const titleText = (
    <Text variant={Text.VARIANTS.SM} weight={style.titleWeight} className={cn('tracking-[0.01em]', style.title)}>
      {entry.title}
    </Text>
  );

  return (
    <div className="agent-timeline-entry relative ml-8 pb-16 last:pb-0">
      {/* Timeline line */}
      {!isLast && (
        <div
          data-testid="timeline-line"
          className={cn(
            'absolute left-[5px] top-[20px] z-[1] w-[2px] bg-blue-300 transition-all duration-300 ease-in-out',
            expanded ? 'bottom-0' : 'bottom-[4px]'
          )}
        />
      )}

      {/* Timeline dot */}
      <div
        data-testid="timeline-dot"
        className={cn(
          'absolute left-0 top-[4px] z-[2] flex h-[12px] w-[12px] items-center justify-center rounded-full border border-solid',
          style.dot,
          {
            'cursor-pointer': hasContent,
          }
        )}
        onClick={handleToggle}
        role={hasContent ? 'button' : undefined}
        onKeyDown={
          hasContent
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggle();
                }
              }
            : undefined
        }
      >
        {style.icon}
      </div>

      {/* Content */}
      <div className="ml-24">
        {hasContent ? (
          <Accordion
            expanded={expanded}
            onChange={handleToggle}
            elevation={0}
            disableGutters
            className="bg-transparent p-0 shadow-none before:hidden"
          >
            <AccordionSummary
              className="mt-0 min-h-0 cursor-pointer p-0 leading-[20px] transition-colors duration-200 hover:text-blue-1000"
              sx={{
                '& .MuiAccordionSummary-content': {
                  margin: 0,
                },
              }}
            >
              {titleText}
            </AccordionSummary>

            <AccordionDetails>
              {typeof entry.content === 'string' ? (
                <CliveMarkdownDisplay content={entry.content} className="text-grey-600" />
              ) : (
                entry.content!
              )}
            </AccordionDetails>
          </Accordion>
        ) : (
          <div className="leading-[20px]">{titleText}</div>
        )}
      </div>
    </div>
  );
};

export default AgentTimelineEntryDisplay;
